// Learning Layer - SaaS Metrics Education Content
// Based on RevOps AI methodology
// All user-facing strings use i18n keys

import { UserRole } from '@/contexts/RoleContext';

export type MetricCategory = 'growth' | 'funnel' | 'efficiency' | 'retention';

export interface MetricBenchmark {
  good: string;
  average: string;
  poor: string;
}

export interface RoleContent {
  context: string;
  focus: string;
}

export interface LearningMetric {
  id: string;
  nameKey: string;
  shortName?: string;
  category: MetricCategory;
  whatIsItKey: string;
  whyItMattersKey: string;
  wbdViewKey: string;
  benchmarks: MetricBenchmark; // keys
  howToImproveKeys: string[];
  roleContent: {
    executive: { contextKey: string; focusKey: string };
    manager: { contextKey: string; focusKey: string };
    rep: { contextKey: string; focusKey: string };
  };
  relatedDashboards: string[];
  relatedPlaybooks?: string[];
  alertTriggers?: string[];
  formulaKey?: string;
  unit: string;
}

const m = (id: string) => `learning.metrics.${id}`;

export const learningMetrics: LearningMetric[] = [
  // ========== GROWTH & REVENUE ==========
  {
    id: 'closed-revenue',
    nameKey: m('closedRevenue.name'),
    category: 'growth',
    whatIsItKey: m('closedRevenue.whatIsIt'),
    whyItMattersKey: m('closedRevenue.whyItMatters'),
    wbdViewKey: m('closedRevenue.wbdView'),
    benchmarks: {
      good: m('closedRevenue.benchmarks.good'),
      average: m('closedRevenue.benchmarks.average'),
      poor: m('closedRevenue.benchmarks.poor'),
    },
    howToImproveKeys: [
      m('closedRevenue.improve.0'), m('closedRevenue.improve.1'),
      m('closedRevenue.improve.2'), m('closedRevenue.improve.3'),
    ],
    roleContent: {
      executive: { contextKey: m('closedRevenue.executive.context'), focusKey: m('closedRevenue.executive.focus') },
      manager: { contextKey: m('closedRevenue.manager.context'), focusKey: m('closedRevenue.manager.focus') },
      rep: { contextKey: m('closedRevenue.rep.context'), focusKey: m('closedRevenue.rep.focus') },
    },
    relatedDashboards: ['/executive-overview', '/sales-burndown', '/forecast'],
    formulaKey: m('closedRevenue.formula'),
    unit: '$',
  },
  {
    id: 'monthly-target',
    nameKey: m('monthlyTarget.name'),
    category: 'growth',
    whatIsItKey: m('monthlyTarget.whatIsIt'),
    whyItMattersKey: m('monthlyTarget.whyItMatters'),
    wbdViewKey: m('monthlyTarget.wbdView'),
    benchmarks: {
      good: m('monthlyTarget.benchmarks.good'),
      average: m('monthlyTarget.benchmarks.average'),
      poor: m('monthlyTarget.benchmarks.poor'),
    },
    howToImproveKeys: [
      m('monthlyTarget.improve.0'), m('monthlyTarget.improve.1'),
      m('monthlyTarget.improve.2'), m('monthlyTarget.improve.3'),
    ],
    roleContent: {
      executive: { contextKey: m('monthlyTarget.executive.context'), focusKey: m('monthlyTarget.executive.focus') },
      manager: { contextKey: m('monthlyTarget.manager.context'), focusKey: m('monthlyTarget.manager.focus') },
      rep: { contextKey: m('monthlyTarget.rep.context'), focusKey: m('monthlyTarget.rep.focus') },
    },
    relatedDashboards: ['/executive-overview', '/targets', '/sales-burndown'],
    formulaKey: m('monthlyTarget.formula'),
    unit: '$',
  },
  {
    id: 'mrr',
    nameKey: m('mrr.name'),
    shortName: 'MRR',
    category: 'growth',
    whatIsItKey: m('mrr.whatIsIt'),
    whyItMattersKey: m('mrr.whyItMatters'),
    wbdViewKey: m('mrr.wbdView'),
    benchmarks: {
      good: m('mrr.benchmarks.good'),
      average: m('mrr.benchmarks.average'),
      poor: m('mrr.benchmarks.poor'),
    },
    howToImproveKeys: [
      m('mrr.improve.0'), m('mrr.improve.1'), m('mrr.improve.2'), m('mrr.improve.3'),
    ],
    roleContent: {
      executive: { contextKey: m('mrr.executive.context'), focusKey: m('mrr.executive.focus') },
      manager: { contextKey: m('mrr.manager.context'), focusKey: m('mrr.manager.focus') },
      rep: { contextKey: m('mrr.rep.context'), focusKey: m('mrr.rep.focus') },
    },
    relatedDashboards: ['/revenue', '/growth', '/forecast'],
    formulaKey: m('mrr.formula'),
    unit: '$',
  },
  {
    id: 'arr',
    nameKey: m('arr.name'),
    shortName: 'ARR',
    category: 'growth',
    whatIsItKey: m('arr.whatIsIt'),
    whyItMattersKey: m('arr.whyItMatters'),
    wbdViewKey: m('arr.wbdView'),
    benchmarks: {
      good: m('arr.benchmarks.good'),
      average: m('arr.benchmarks.average'),
      poor: m('arr.benchmarks.poor'),
    },
    howToImproveKeys: [
      m('arr.improve.0'), m('arr.improve.1'), m('arr.improve.2'), m('arr.improve.3'),
    ],
    roleContent: {
      executive: { contextKey: m('arr.executive.context'), focusKey: m('arr.executive.focus') },
      manager: { contextKey: m('arr.manager.context'), focusKey: m('arr.manager.focus') },
      rep: { contextKey: m('arr.rep.context'), focusKey: m('arr.rep.focus') },
    },
    relatedDashboards: ['/revenue', '/growth', '/forecast'],
    formulaKey: m('arr.formula'),
    unit: '$',
  },
  {
    id: 'expansion-revenue',
    nameKey: m('expansionRevenue.name'),
    category: 'growth',
    whatIsItKey: m('expansionRevenue.whatIsIt'),
    whyItMattersKey: m('expansionRevenue.whyItMatters'),
    wbdViewKey: m('expansionRevenue.wbdView'),
    benchmarks: {
      good: m('expansionRevenue.benchmarks.good'),
      average: m('expansionRevenue.benchmarks.average'),
      poor: m('expansionRevenue.benchmarks.poor'),
    },
    howToImproveKeys: [
      m('expansionRevenue.improve.0'), m('expansionRevenue.improve.1'),
      m('expansionRevenue.improve.2'), m('expansionRevenue.improve.3'),
    ],
    roleContent: {
      executive: { contextKey: m('expansionRevenue.executive.context'), focusKey: m('expansionRevenue.executive.focus') },
      manager: { contextKey: m('expansionRevenue.manager.context'), focusKey: m('expansionRevenue.manager.focus') },
      rep: { contextKey: m('expansionRevenue.rep.context'), focusKey: m('expansionRevenue.rep.focus') },
    },
    relatedDashboards: ['/growth', '/revenue'],
    unit: '$',
  },

  // ========== FUNNEL & SALES ==========
  {
    id: 'new-mqls',
    nameKey: m('newMqls.name'),
    shortName: 'New MQLs',
    category: 'funnel',
    whatIsItKey: m('newMqls.whatIsIt'),
    whyItMattersKey: m('newMqls.whyItMatters'),
    wbdViewKey: m('newMqls.wbdView'),
    benchmarks: {
      good: m('newMqls.benchmarks.good'),
      average: m('newMqls.benchmarks.average'),
      poor: m('newMqls.benchmarks.poor'),
    },
    howToImproveKeys: [
      m('newMqls.improve.0'), m('newMqls.improve.1'),
      m('newMqls.improve.2'), m('newMqls.improve.3'),
    ],
    roleContent: {
      executive: { contextKey: m('newMqls.executive.context'), focusKey: m('newMqls.executive.focus') },
      manager: { contextKey: m('newMqls.manager.context'), focusKey: m('newMqls.manager.focus') },
      rep: { contextKey: m('newMqls.rep.context'), focusKey: m('newMqls.rep.focus') },
    },
    relatedDashboards: ['/analytics', '/funnel'],
    relatedPlaybooks: ['Inbound Lead Handling'],
    unit: '#',
  },
  {
    id: 'lead-to-opp-conversion',
    nameKey: m('leadToOpp.name'),
    shortName: 'L→O Conversion',
    category: 'funnel',
    whatIsItKey: m('leadToOpp.whatIsIt'),
    whyItMattersKey: m('leadToOpp.whyItMatters'),
    wbdViewKey: m('leadToOpp.wbdView'),
    benchmarks: {
      good: m('leadToOpp.benchmarks.good'),
      average: m('leadToOpp.benchmarks.average'),
      poor: m('leadToOpp.benchmarks.poor'),
    },
    howToImproveKeys: [
      m('leadToOpp.improve.0'), m('leadToOpp.improve.1'),
      m('leadToOpp.improve.2'), m('leadToOpp.improve.3'),
    ],
    roleContent: {
      executive: { contextKey: m('leadToOpp.executive.context'), focusKey: m('leadToOpp.executive.focus') },
      manager: { contextKey: m('leadToOpp.manager.context'), focusKey: m('leadToOpp.manager.focus') },
      rep: { contextKey: m('leadToOpp.rep.context'), focusKey: m('leadToOpp.rep.focus') },
    },
    relatedDashboards: ['/analytics', '/funnel'],
    relatedPlaybooks: ['Inbound Lead Handling', 'Outbound Prospecting'],
    unit: '%',
  },
  {
    id: 'win-rate',
    nameKey: m('winRate.name'),
    category: 'funnel',
    whatIsItKey: m('winRate.whatIsIt'),
    whyItMattersKey: m('winRate.whyItMatters'),
    wbdViewKey: m('winRate.wbdView'),
    benchmarks: {
      good: m('winRate.benchmarks.good'),
      average: m('winRate.benchmarks.average'),
      poor: m('winRate.benchmarks.poor'),
    },
    howToImproveKeys: [
      m('winRate.improve.0'), m('winRate.improve.1'), m('winRate.improve.2'),
      m('winRate.improve.3'), m('winRate.improve.4'),
    ],
    roleContent: {
      executive: { contextKey: m('winRate.executive.context'), focusKey: m('winRate.executive.focus') },
      manager: { contextKey: m('winRate.manager.context'), focusKey: m('winRate.manager.focus') },
      rep: { contextKey: m('winRate.rep.context'), focusKey: m('winRate.rep.focus') },
    },
    relatedDashboards: ['/analytics', '/pipeline'],
    alertTriggers: ['Win rate drops below 20%'],
    unit: '%',
  },
  {
    id: 'sales-cycle',
    nameKey: m('salesCycle.name'),
    category: 'funnel',
    whatIsItKey: m('salesCycle.whatIsIt'),
    whyItMattersKey: m('salesCycle.whyItMatters'),
    wbdViewKey: m('salesCycle.wbdView'),
    benchmarks: {
      good: m('salesCycle.benchmarks.good'),
      average: m('salesCycle.benchmarks.average'),
      poor: m('salesCycle.benchmarks.poor'),
    },
    howToImproveKeys: [
      m('salesCycle.improve.0'), m('salesCycle.improve.1'), m('salesCycle.improve.2'),
      m('salesCycle.improve.3'), m('salesCycle.improve.4'),
    ],
    roleContent: {
      executive: { contextKey: m('salesCycle.executive.context'), focusKey: m('salesCycle.executive.focus') },
      manager: { contextKey: m('salesCycle.manager.context'), focusKey: m('salesCycle.manager.focus') },
      rep: { contextKey: m('salesCycle.rep.context'), focusKey: m('salesCycle.rep.focus') },
    },
    relatedDashboards: ['/analytics', '/pipeline'],
    relatedPlaybooks: ['Deal Acceleration'],
    unit: 'days',
  },
  {
    id: 'pipeline-coverage',
    nameKey: m('pipelineCoverage.name'),
    category: 'funnel',
    whatIsItKey: m('pipelineCoverage.whatIsIt'),
    whyItMattersKey: m('pipelineCoverage.whyItMatters'),
    wbdViewKey: m('pipelineCoverage.wbdView'),
    benchmarks: {
      good: m('pipelineCoverage.benchmarks.good'),
      average: m('pipelineCoverage.benchmarks.average'),
      poor: m('pipelineCoverage.benchmarks.poor'),
    },
    howToImproveKeys: [
      m('pipelineCoverage.improve.0'), m('pipelineCoverage.improve.1'),
      m('pipelineCoverage.improve.2'), m('pipelineCoverage.improve.3'),
      m('pipelineCoverage.improve.4'),
    ],
    roleContent: {
      executive: { contextKey: m('pipelineCoverage.executive.context'), focusKey: m('pipelineCoverage.executive.focus') },
      manager: { contextKey: m('pipelineCoverage.manager.context'), focusKey: m('pipelineCoverage.manager.focus') },
      rep: { contextKey: m('pipelineCoverage.rep.context'), focusKey: m('pipelineCoverage.rep.focus') },
    },
    relatedDashboards: ['/pipeline', '/forecast'],
    alertTriggers: ['Coverage drops below 2x'],
    unit: 'x',
  },
  {
    id: 'forecast-accuracy',
    nameKey: m('forecastAccuracy.name'),
    category: 'funnel',
    whatIsItKey: m('forecastAccuracy.whatIsIt'),
    whyItMattersKey: m('forecastAccuracy.whyItMatters'),
    wbdViewKey: m('forecastAccuracy.wbdView'),
    benchmarks: {
      good: m('forecastAccuracy.benchmarks.good'),
      average: m('forecastAccuracy.benchmarks.average'),
      poor: m('forecastAccuracy.benchmarks.poor'),
    },
    howToImproveKeys: [
      m('forecastAccuracy.improve.0'), m('forecastAccuracy.improve.1'),
      m('forecastAccuracy.improve.2'), m('forecastAccuracy.improve.3'),
      m('forecastAccuracy.improve.4'),
    ],
    roleContent: {
      executive: { contextKey: m('forecastAccuracy.executive.context'), focusKey: m('forecastAccuracy.executive.focus') },
      manager: { contextKey: m('forecastAccuracy.manager.context'), focusKey: m('forecastAccuracy.manager.focus') },
      rep: { contextKey: m('forecastAccuracy.rep.context'), focusKey: m('forecastAccuracy.rep.focus') },
    },
    relatedDashboards: ['/forecast'],
    unit: '%',
  },

  // ========== EFFICIENCY ==========
  {
    id: 'acv',
    nameKey: m('acv.name'),
    shortName: 'ACV',
    category: 'efficiency',
    whatIsItKey: m('acv.whatIsIt'),
    whyItMattersKey: m('acv.whyItMatters'),
    wbdViewKey: m('acv.wbdView'),
    benchmarks: {
      good: m('acv.benchmarks.good'),
      average: m('acv.benchmarks.average'),
      poor: m('acv.benchmarks.poor'),
    },
    howToImproveKeys: [
      m('acv.improve.0'), m('acv.improve.1'), m('acv.improve.2'),
      m('acv.improve.3'), m('acv.improve.4'),
    ],
    roleContent: {
      executive: { contextKey: m('acv.executive.context'), focusKey: m('acv.executive.focus') },
      manager: { contextKey: m('acv.manager.context'), focusKey: m('acv.manager.focus') },
      rep: { contextKey: m('acv.rep.context'), focusKey: m('acv.rep.focus') },
    },
    relatedDashboards: ['/analytics', '/revenue'],
    formulaKey: m('acv.formula'),
    unit: '$',
  },
  {
    id: 'revenue-per-lead',
    nameKey: m('rpl.name'),
    shortName: 'RPL',
    category: 'efficiency',
    whatIsItKey: m('rpl.whatIsIt'),
    whyItMattersKey: m('rpl.whyItMatters'),
    wbdViewKey: m('rpl.wbdView'),
    benchmarks: {
      good: m('rpl.benchmarks.good'),
      average: m('rpl.benchmarks.average'),
      poor: m('rpl.benchmarks.poor'),
    },
    howToImproveKeys: [
      m('rpl.improve.0'), m('rpl.improve.1'), m('rpl.improve.2'), m('rpl.improve.3'),
    ],
    roleContent: {
      executive: { contextKey: m('rpl.executive.context'), focusKey: m('rpl.executive.focus') },
      manager: { contextKey: m('rpl.manager.context'), focusKey: m('rpl.manager.focus') },
      rep: { contextKey: m('rpl.rep.context'), focusKey: m('rpl.rep.focus') },
    },
    relatedDashboards: ['/analytics'],
    formulaKey: m('rpl.formula'),
    unit: '$',
  },
  {
    id: 'time-to-first-contact',
    nameKey: m('ttfc.name'),
    shortName: 'TTFC',
    category: 'efficiency',
    whatIsItKey: m('ttfc.whatIsIt'),
    whyItMattersKey: m('ttfc.whyItMatters'),
    wbdViewKey: m('ttfc.wbdView'),
    benchmarks: {
      good: m('ttfc.benchmarks.good'),
      average: m('ttfc.benchmarks.average'),
      poor: m('ttfc.benchmarks.poor'),
    },
    howToImproveKeys: [
      m('ttfc.improve.0'), m('ttfc.improve.1'), m('ttfc.improve.2'),
      m('ttfc.improve.3'), m('ttfc.improve.4'),
    ],
    roleContent: {
      executive: { contextKey: m('ttfc.executive.context'), focusKey: m('ttfc.executive.focus') },
      manager: { contextKey: m('ttfc.manager.context'), focusKey: m('ttfc.manager.focus') },
      rep: { contextKey: m('ttfc.rep.context'), focusKey: m('ttfc.rep.focus') },
    },
    relatedDashboards: ['/analytics'],
    relatedPlaybooks: ['Inbound Lead Handling', 'Chatbot First Response'],
    alertTriggers: ['TTFC exceeds 30 minutes'],
    unit: 'min',
  },

  // ========== RETENTION ==========
  {
    id: 'churn-rate',
    nameKey: m('churnRate.name'),
    category: 'retention',
    whatIsItKey: m('churnRate.whatIsIt'),
    whyItMattersKey: m('churnRate.whyItMatters'),
    wbdViewKey: m('churnRate.wbdView'),
    benchmarks: {
      good: m('churnRate.benchmarks.good'),
      average: m('churnRate.benchmarks.average'),
      poor: m('churnRate.benchmarks.poor'),
    },
    howToImproveKeys: [
      m('churnRate.improve.0'), m('churnRate.improve.1'), m('churnRate.improve.2'),
      m('churnRate.improve.3'), m('churnRate.improve.4'),
    ],
    roleContent: {
      executive: { contextKey: m('churnRate.executive.context'), focusKey: m('churnRate.executive.focus') },
      manager: { contextKey: m('churnRate.manager.context'), focusKey: m('churnRate.manager.focus') },
      rep: { contextKey: m('churnRate.rep.context'), focusKey: m('churnRate.rep.focus') },
    },
    relatedDashboards: ['/growth', '/revenue'],
    alertTriggers: ['Churn rate exceeds 10%'],
    unit: '%',
  },
  {
    id: 'nrr',
    nameKey: m('nrr.name'),
    shortName: 'NRR',
    category: 'retention',
    whatIsItKey: m('nrr.whatIsIt'),
    whyItMattersKey: m('nrr.whyItMatters'),
    wbdViewKey: m('nrr.wbdView'),
    benchmarks: {
      good: m('nrr.benchmarks.good'),
      average: m('nrr.benchmarks.average'),
      poor: m('nrr.benchmarks.poor'),
    },
    howToImproveKeys: [
      m('nrr.improve.0'), m('nrr.improve.1'), m('nrr.improve.2'), m('nrr.improve.3'),
    ],
    roleContent: {
      executive: { contextKey: m('nrr.executive.context'), focusKey: m('nrr.executive.focus') },
      manager: { contextKey: m('nrr.manager.context'), focusKey: m('nrr.manager.focus') },
      rep: { contextKey: m('nrr.rep.context'), focusKey: m('nrr.rep.focus') },
    },
    relatedDashboards: ['/growth', '/revenue'],
    formulaKey: m('nrr.formula'),
    unit: '%',
  },
  {
    id: 'grr',
    nameKey: m('grr.name'),
    shortName: 'GRR',
    category: 'retention',
    whatIsItKey: m('grr.whatIsIt'),
    whyItMattersKey: m('grr.whyItMatters'),
    wbdViewKey: m('grr.wbdView'),
    benchmarks: {
      good: m('grr.benchmarks.good'),
      average: m('grr.benchmarks.average'),
      poor: m('grr.benchmarks.poor'),
    },
    howToImproveKeys: [
      m('grr.improve.0'), m('grr.improve.1'), m('grr.improve.2'),
      m('grr.improve.3'), m('grr.improve.4'),
    ],
    roleContent: {
      executive: { contextKey: m('grr.executive.context'), focusKey: m('grr.executive.focus') },
      manager: { contextKey: m('grr.manager.context'), focusKey: m('grr.manager.focus') },
      rep: { contextKey: m('grr.rep.context'), focusKey: m('grr.rep.focus') },
    },
    relatedDashboards: ['/growth', '/revenue'],
    formulaKey: m('grr.formula'),
    unit: '%',
  },
];

// Utility functions
export function getMetricById(id: string): LearningMetric | undefined {
  return learningMetrics.find(m => m.id === id);
}

export function getMetricsByCategory(category: MetricCategory): LearningMetric[] {
  return learningMetrics.filter(m => m.category === category);
}

export function getContentForRole(metric: LearningMetric, role: UserRole) {
  const roleMap: Record<UserRole, keyof typeof metric.roleContent> = {
    executive: 'executive',
    manager: 'manager',
    rep: 'rep',
  };
  return metric.roleContent[roleMap[role]];
}

// Category label keys (translated in components)
export const categoryLabelKeys: Record<MetricCategory, string> = {
  growth: 'learning.categories.growth',
  funnel: 'learning.categories.funnel',
  efficiency: 'learning.categories.efficiency',
  retention: 'learning.categories.retention',
};

export const categoryColors: Record<MetricCategory, string> = {
  growth: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  funnel: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  efficiency: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  retention: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
};
